import { useState } from 'react';

import { Card } from '../../../common/Card';
import { Dialog } from '../../../common/Dialog';
import { Icon } from '../../../common/Icon';
import { MenuButton, MenuOption } from '../../../common/MenuButton';
import { formatMarkdownContent } from '../../../common/TipTapEditor';
import { AdhocUpdate } from '../types';

import { AdhocUpdateDetailView } from './AdhocUpdateDetailView';

interface AdhocUpdateCardProps {
  item: AdhocUpdate;
  hideMenu?: boolean;
  showDescription?: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  actionButton?: JSX.Element;
}

export function AdhocUpdateCard({
  item,
  hideMenu = false,
  showDescription = false,
  onClick,
  onEdit,
  onDelete,
  actionButton,
}: AdhocUpdateCardProps): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDelete = (): void => {
    if (window.confirm('Are you sure you want to delete this ad-hoc update?')) {
      try {
        onDelete?.();
      } catch (error) {
        console.error('Failed to delete ad-hoc update:', error);
      }
    }
  };

  const menuOptions = [
    { title: 'Edit', value: 'edit', onClick: onEdit },
    { title: 'Delete', value: 'delete', onClick: handleDelete, color: 'red' },
  ].filter((option): option is MenuOption => option.onClick !== undefined);

  return (
    <>
      <div className="flex items-center w-full">
        <Card className="w-full bg-[#FEFDFC]   flex flex-col flex-wrap gap-2 justify-start cursor-pointer select-text hover:bg-gray-50">
          <div
            className="flex flex-col w-full gap-2 cursor-pointer flex-wrap"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Icon name="timer" className="text-[#FA8C16]" />
                <h3 className="text-md font-normal leading-5 text-label truncate">
                  {item.title}
                </h3>
              </div>
              {!hideMenu && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <MenuButton options={menuOptions} direction="left" />
                </div>
              )}
            </div>
            {showDescription && (
              <p className="w-full text-md font-normal leading-6 text-label break-words whitespace-pre-wrap line-clamp-4">
                {formatMarkdownContent(item.description)}
              </p>
            )}
          </div>
          {actionButton && actionButton}
        </Card>
      </div>

      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <Icon name="timer" className="text-[#FA8C16]" />
            <span>{item.title}</span>
          </div>
        }
      >
        <AdhocUpdateDetailView item={item} />
      </Dialog>
    </>
  );
}
